import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Typography,
  IconButton,
  Chip,
  TextField,
  FormControlLabel,
  Switch,
  Divider,
} from '@mui/material';
import { Close, DeleteOutline, Warning } from '@mui/icons-material';
import { apiService } from '../services/apiService';

interface BulkDeleteSessionDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  studioId: string;
}

const DAYS = [
  { label: 'Sun', value: 0 },
  { label: 'Mon', value: 1 },
  { label: 'Tue', value: 2 },
  { label: 'Wed', value: 3 },
  { label: 'Thu', value: 4 },
  { label: 'Fri', value: 5 },
  { label: 'Sat', value: 6 },
];

const BulkDeleteSessionDialog: React.FC<BulkDeleteSessionDialogProps> = ({
  open,
  onClose,
  onSuccess,
  studioId,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ deletedCount: number; skippedCount: number; skippedSessions: string[] } | null>(null);
  const [classes, setClasses] = useState<any[]>([]);

  const [formData, setFormData] = useState({
    class: '',
    startDate: '',
    endDate: '',
    daysOfWeek: [0, 1, 2, 3, 4, 5, 6] as number[],
    trialOnly: false,
  });

  const fetchClasses = useCallback(async () => {
    try {
      const res = await apiService.getStudioClasses(studioId);
      setClasses(res.classes || []);
    } catch (e) {
      console.error('Failed to fetch classes', e);
    }
  }, [studioId]);

  useEffect(() => {
    if (open) {
      fetchClasses();
      setError(null);
      setResult(null);
      setFormData({
        class: '',
        startDate: '',
        endDate: '',
        daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
        trialOnly: false,
      });
    }
  }, [open, fetchClasses]);

  const toggleDay = (day: number) => {
    setFormData(prev => ({
      ...prev,
      daysOfWeek: prev.daysOfWeek.includes(day)
        ? prev.daysOfWeek.filter(d => d !== day)
        : [...prev.daysOfWeek, day].sort((a, b) => a - b),
    }));
  };

  // Estimated days in range matching the filter
  const estimatedDays = React.useMemo(() => {
    if (!formData.startDate || !formData.endDate || formData.daysOfWeek.length === 0) return 0;
    const start = new Date(formData.startDate + 'T00:00:00');
    const end = new Date(formData.endDate + 'T00:00:00');
    if (end < start) return 0;
    let count = 0;
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      if (formData.daysOfWeek.includes(d.getDay())) count++;
    }
    return count;
  }, [formData.startDate, formData.endDate, formData.daysOfWeek]);

  const handleDelete = async () => {
    if (!formData.class) { setError('Please select a class'); return; }
    if (!formData.startDate) { setError('Please select a start date'); return; }
    if (!formData.endDate) { setError('Please select an end date'); return; }
    if (formData.daysOfWeek.length === 0) { setError('Please select at least one day'); return; }

    try {
      setLoading(true);
      setError(null);
      const res = await apiService.bulkDeleteByRange(studioId, {
        class: formData.class,
        startDate: formData.startDate,
        endDate: formData.endDate,
        daysOfWeek: formData.daysOfWeek,
        trialOnly: formData.trialOnly,
      });
      setResult(res);
      onSuccess();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to delete sessions');
    } finally {
      setLoading(false);
    }
  };

  const selectedClassName = classes.find(c => c._id === formData.class)?.className;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <DeleteOutline color="error" />
            <Typography variant="h6">Bulk Delete Sessions</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {result ? (
          <Box>
            <Alert severity={result.deletedCount > 0 ? 'success' : 'info'} sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight={600}>
                {result.deletedCount} session{result.deletedCount !== 1 ? 's' : ''} deleted
                {result.skippedCount > 0 && `, ${result.skippedCount} skipped (have active bookings)`}
              </Typography>
            </Alert>
            {result.skippedSessions.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  Skipped sessions (active bookings):
                </Typography>
                {result.skippedSessions.map((s, i) => (
                  <Typography key={i} variant="caption" display="block" color="warning.main">• {s}</Typography>
                ))}
              </Box>
            )}
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={2.5}>
            <Alert severity="warning" icon={<Warning />}>
              Sessions with active bookings will be skipped. All others will be permanently deleted.
            </Alert>

            {/* Class */}
            <FormControl fullWidth size="small">
              <InputLabel>Class *</InputLabel>
              <Select
                value={formData.class}
                onChange={e => setFormData(prev => ({ ...prev, class: e.target.value }))}
                label="Class *"
              >
                {classes.map(c => (
                  <MenuItem key={c._id} value={c._id}>{c.className}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Date range */}
            <Box display="flex" gap={2}>
              <TextField
                label="Start Date *"
                type="date"
                value={formData.startDate}
                onChange={e => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="End Date *"
                type="date"
                value={formData.endDate}
                onChange={e => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: formData.startDate }}
              />
            </Box>

            {/* Days of week */}
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="body2" fontWeight={600}>Days of Week *</Typography>
                <Box display="flex" gap={1}>
                  <Button size="small" variant="text" onClick={() => setFormData(prev => ({ ...prev, daysOfWeek: [0,1,2,3,4,5,6] }))} sx={{ minWidth: 0, p: 0.5, fontSize: 11 }}>All</Button>
                  <Button size="small" variant="text" onClick={() => setFormData(prev => ({ ...prev, daysOfWeek: [] }))} sx={{ minWidth: 0, p: 0.5, fontSize: 11 }}>Clear</Button>
                </Box>
              </Box>
              <Box display="flex" gap={1} flexWrap="wrap">
                {DAYS.map(d => (
                  <Chip
                    key={d.value}
                    label={d.label}
                    onClick={() => toggleDay(d.value)}
                    color={formData.daysOfWeek.includes(d.value) ? 'error' : 'default'}
                    variant={formData.daysOfWeek.includes(d.value) ? 'filled' : 'outlined'}
                    size="small"
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Box>

            <Divider />

            {/* Trial filter */}
            <FormControlLabel
              control={
                <Switch
                  checked={formData.trialOnly}
                  onChange={e => setFormData(prev => ({ ...prev, trialOnly: e.target.checked }))}
                  size="small"
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Trial sessions only</Typography>
                  <Typography variant="caption" color="text.secondary">
                    When enabled, only sessions marked as trial will be deleted
                  </Typography>
                </Box>
              }
            />

            {/* Preview */}
            {estimatedDays > 0 && formData.class && (
              <Alert severity="error" icon={false}>
                <Typography variant="body2">
                  {formData.trialOnly ? 'Trial sessions' : 'Sessions'} of{' '}
                  <strong>{selectedClassName}</strong> across{' '}
                  <strong>{estimatedDays} day{estimatedDays !== 1 ? 's' : ''}</strong> from{' '}
                  <strong>{formData.startDate}</strong> to <strong>{formData.endDate}</strong> will be deleted
                  (booked sessions will be skipped).
                </Typography>
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          {result ? 'Close' : 'Cancel'}
        </Button>
        {!result && (
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={loading || estimatedDays === 0 || !formData.class}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <DeleteOutline />}
          >
            {loading ? 'Deleting…' : `Delete Sessions`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkDeleteSessionDialog;
